import React, { useState, useEffect } from "react";
import { Table, Switch, Text, Card, Title, Stack, Radio, Badge, Group, Select, Loader, Alert } from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";
import { CHAIRPERSON_VOTE_MAX_ATTENDANCE, isChairpersonVoteIncluded } from "@hmh-proc/shared";
import { useMarkAttendance, useMeeting } from "../hooks";
import { userApi } from "../../../api/users";

interface AttendanceTabProps {
  meetingId: string;
}

export const AttendanceTab: React.FC<AttendanceTabProps> = ({ meetingId }) => {
  const { data: meeting } = useMeeting(meetingId);
  const markAttendance = useMarkAttendance();
  const [chairpersonId, setChairpersonId] = useState<string | null>(null);
  const [secretaryId, setSecretaryId] = useState<string | null>(null);
  const [procurementOfficers, setProcurementOfficers] = useState<Array<{ value: string; label: string }>>([]);
  const [loadingOfficers, setLoadingOfficers] = useState(false);

  // Initialize chairperson and secretary from meeting data
  useEffect(() => {
    if (meeting?.attendances) {
      const chairperson = meeting.attendances.find(att => att.isChairperson);
      if (chairperson) {
        setChairpersonId(chairperson.memberId);
      }
      const secretary = meeting.attendances.find(att => att.isSecretary);
      if (secretary) {
        setSecretaryId(secretary.memberId);
      }
    }
  }, [meeting]);

  // Load procurement officers
  useEffect(() => {
    const loadProcurementOfficers = async () => {
      setLoadingOfficers(true);
      try {
        const users = await userApi.getUsersForEvaluationReport();
        const officers = users
          .filter((u) => u.role === 'PROCUREMENT_OFFICER')
          .map((u) => ({ value: u.id, label: u.fullName }));
        setProcurementOfficers(officers);
      } catch (error) {
        console.error('Error loading procurement officers:', error);
      } finally {
        setLoadingOfficers(false);
      }
    };
    loadProcurementOfficers();
  }, []);

  const handleToggleAttendance = (memberId: string, presence: boolean) => {
    markAttendance.mutate({
      meetingId,
      data: {
        memberId,
        presence,
        allowVote: presence ? undefined : false,
      },
    });
  };

  const handleToggleAllowVote = (memberId: string, allowVote: boolean) => {
    const attendance = meeting?.attendances?.find((att) => att.memberId === memberId);
    markAttendance.mutate({
      meetingId,
      data: {
        memberId,
        presence: attendance?.presence || false,
        allowVote,
      },
    });
  };

  const handleChairpersonChange = (memberId: string) => {
    // Prevent selecting the same person as secretary
    if (memberId === secretaryId) {
      return;
    }
    setChairpersonId(memberId);
    // Find the attendance record to get presence status
    const attendance = meeting?.attendances?.find(att => att.memberId === memberId);
    markAttendance.mutate({
      meetingId,
      data: { 
        memberId, 
        presence: attendance?.presence || false,
        allowVote: attendance?.allowVote || false,
        isChairperson: true,
      },
    });
  };

  const handleSecretaryChange = (memberId: string | null) => {
    // Prevent selecting the same person as chairperson
    if (memberId === chairpersonId) {
      return;
    }
    setSecretaryId(memberId);
    if (memberId) {
      // Find the attendance record to get presence status, or create one if not exists
      const attendance = meeting?.attendances?.find(att => att.memberId === memberId);
      markAttendance.mutate({
        meetingId,
        data: { 
          memberId, 
          presence: attendance?.presence || true, // Default to present if not in attendance
          allowVote: attendance?.allowVote || false,
          isSecretary: true,
        },
      });
    } else {
      // Clear secretary
      if (secretaryId) {
        const attendance = meeting?.attendances?.find(att => att.memberId === secretaryId);
        markAttendance.mutate({
          meetingId,
          data: { 
            memberId: secretaryId, 
            presence: attendance?.presence || false,
            allowVote: attendance?.allowVote || false,
            isSecretary: false,
          },
        });
      }
    }
  };

  if (!meeting?.attendances || meeting.attendances.length === 0) {
    return (
      <Card>
        <Stack gap="md">
          <Title order={3}>Meeting Attendance</Title>
          <Text c="dimmed">No members assigned to this meeting.</Text>
        </Stack>
      </Card>
    );
  }

  const chairVotes = isChairpersonVoteIncluded(meeting.attendances);

  // Filter out chairperson from secretary options
  const secretaryOptions = procurementOfficers.filter(
    (officer) => officer.value !== chairpersonId
  );

  return (
    <Card>
      <Stack gap="md">
        <Title order={3}>Meeting Attendance</Title>

        <Stack gap="md">
          <Group>
            <Text fw={500} size="sm">Secretary:</Text>
            {loadingOfficers ? (
              <Loader size="sm" />
            ) : (
              <Select
                placeholder="Select procurement officer as secretary"
                data={secretaryOptions}
                value={secretaryId || null}
                onChange={handleSecretaryChange}
                disabled={markAttendance.isPending}
                clearable
                style={{ flex: 1, maxWidth: 300 }}
              />
            )}
          </Group>
          {chairpersonId && secretaryId === chairpersonId && (
            <Alert icon={<IconAlertCircle size={16} />} color="red">
              Chairperson cannot be the secretary. Please select a different person as secretary.
            </Alert>
          )}

          <Radio.Group
            value={chairpersonId || ""}
            onChange={(value) => {
              if (value) {
                handleChairpersonChange(value);
              }
            }}
          >
            <Table>
              <thead>
                <tr>
                  <th>Member Name</th>
                  <th>Role</th>
                  <th>Present</th>
                  <th>Allow Vote</th>
                  <th>Chairperson</th>
                </tr>
              </thead>
              <tbody>
                {meeting.attendances.map((attendance) => {
                  const voteRequired = chairVotes && !!attendance.isChairperson;
                  return (
                    <tr key={attendance.memberId}>
                      <td>
                        <Group gap="xs">
                          <Text fw={500}>{attendance.member.fullName}</Text>
                          {attendance.isChairperson && (
                            <Badge size="sm" color="blue" variant="light">
                              Chairperson
                            </Badge>
                          )}
                          {attendance.isSecretary && (
                            <Badge size="sm" color="green" variant="light">
                              Secretary
                            </Badge>
                          )}
                        </Group>
                      </td>
                      <td>
                        <Text size="sm" c="dimmed">
                          {attendance.member.role.replace("_", " ")}
                        </Text>
                      </td>
                      <td>
                        <Switch
                          checked={attendance.presence || false}
                          onChange={(event) =>
                            handleToggleAttendance(
                              attendance.memberId,
                              event.currentTarget.checked
                            )
                          }
                          disabled={markAttendance.isPending}
                        />
                      </td>
                      <td>
                        <Switch
                          checked={attendance.allowVote || voteRequired}
                          onChange={(event) =>
                            handleToggleAllowVote(
                              attendance.memberId,
                              event.currentTarget.checked
                            )
                          }
                          disabled={
                            markAttendance.isPending || !attendance.presence || voteRequired
                          }
                        />
                      </td>
                      <td>
                        <Radio
                          value={attendance.memberId}
                          disabled={
                            markAttendance.isPending || 
                            !attendance.presence || 
                            attendance.memberId === secretaryId
                          }
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </Radio.Group>
        </Stack>
        <Text size="sm" c="dimmed">
          Note: Only present members can be selected as chairperson or allowed to vote. Chairperson cannot be the secretary. Enable Allow Vote for members who should receive award sheets and return forms for approval. When only {CHAIRPERSON_VOTE_MAX_ATTENDANCE} members attend, the chairperson's vote is included automatically.
        </Text>
      </Stack>
    </Card>
  );
};
